/**
 * GedcomFamilyPreview Component
 *
 * Displays parsed FAM records in a table format.
 * Shows family GEDCOM ID, husband, wife, children, marriage, and divorce information.
 * Handles unresolved references gracefully with warnings.
 *
 * Part of Phase 4 - Slice 3 & 4: FAM Record Parsing + Date Parsing
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResolvedFamily } from '../types/GedcomFamily';
import type { DateQualifier } from '../parsers/dateParser';

interface GedcomFamilyPreviewProps {
  families: ResolvedFamily[];
}

export function GedcomFamilyPreview({ families }: GedcomFamilyPreviewProps) {
  const { t, i18n } = useTranslation('common');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (familyId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(familyId)) {
        next.delete(familyId);
      } else {
        next.add(familyId);
      }
      return next;
    });
  };

  /**
   * Format ISO date to readable format with locale support (e.g., "24 May 1819" / "24 مايو 1819")
   */
  const formatDateForDisplay = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-').map(Number);

    // Month names mapping for English and Arabic
    const monthNames: Record<string, { en: string; ar: string }> = {
      '01': { en: 'Jan', ar: 'يناير' },
      '02': { en: 'Feb', ar: 'فبراير' },
      '03': { en: 'Mar', ar: 'مارس' },
      '04': { en: 'Apr', ar: 'أبريل' },
      '05': { en: 'May', ar: 'مايو' },
      '06': { en: 'Jun', ar: 'يونيو' },
      '07': { en: 'Jul', ar: 'يوليو' },
      '08': { en: 'Aug', ar: 'أغسطس' },
      '09': { en: 'Sep', ar: 'سبتمبر' },
      '10': { en: 'Oct', ar: 'أكتوبر' },
      '11': { en: 'Nov', ar: 'نوفمبر' },
      '12': { en: 'Dec', ar: 'ديسمبر' }
    };

    const monthKey = month.toString().padStart(2, '0');
    const monthName = i18n.language === 'ar'
      ? monthNames[monthKey]?.ar
      : monthNames[monthKey]?.en;

    // Return in natural order for both languages
    // English: "24 May 1819"
    // Arabic: "24 مايو 1819" (same order, RTL handles direction)
    return `${day} ${monthName} ${year}`;
  };

  /**
   * Get translated qualifier label
   */
  const getQualifierLabel = (qualifier: DateQualifier): string => {
    switch (qualifier) {
      case 'ABT':
        return t('gedcom.about', 'About');
      case 'BEF':
        return t('gedcom.before', 'Before');
      case 'AFT':
        return t('gedcom.after', 'After');
      case 'CAL':
        return t('gedcom.calculated', 'Calculated');
      case 'EST':
        return t('gedcom.estimated', 'Estimated');
    }
  };

  /**
   * Format date display with qualifier support
   */
  const formatDateDisplay = (
    date: string | null,
    year: number | null,
    qualifier: DateQualifier | null
  ): string => {
    if (!year && !date) {
      return '—';
    }

    let displayValue = '';

    if (date) {
      // Full date available
      displayValue = formatDateForDisplay(date);
    } else if (year) {
      // Year-only
      displayValue = year.toString();
    }

    if (qualifier && displayValue) {
      // Prepend qualifier
      return `${getQualifierLabel(qualifier)} ${displayValue}`;
    }

    return displayValue || '—';
  };

  if (families.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center">
        <span className="material-symbols-outlined text-4xl sm:text-5xl text-slate-400 dark:text-slate-500 mb-3">
          family_restroom
        </span>
        <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
          {t('gedcom.noFamiliesFound', 'No family records found')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Summary */}
      <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
        {t('gedcom.familiesFound', { count: families.length, defaultValue: 'Found {{count}} families' })}
      </div>

      {/* Table */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.familyId', 'Family ID')}
                </th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.husband', 'Husband')}
                </th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.wife', 'Wife')}
                </th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.marriage', 'Marriage')}
                </th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.divorce', 'Divorce')}
                </th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                  {t('gedcom.children', 'Children')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {families.map((family, index) => {
                const isExpanded = expandedRows.has(family.familyId);
                const hasChildren = family.children.length > 0;
                const hasUnresolvedRefs = family.unresolvedRefs.length > 0;

                return (
                  <tr
                    key={family.familyId}
                    className={`${
                      index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900'
                    } hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors`}
                  >
                    {/* Family ID */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                          {family.familyId}
                        </span>
                        {hasUnresolvedRefs && (
                          <span
                            className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-base"
                            title={t('gedcom.unresolvedReferences', {
                              count: family.unresolvedRefs.length,
                              defaultValue: '{{count}} unresolved references',
                            })}
                          >
                            warning
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Husband */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        {family.husband ? (
                          <>
                            <span className="text-base sm:text-lg" title={t('gedcom.male', 'Male')}>
                              ♂
                            </span>
                            <span className="font-medium text-slate-900 dark:text-white">
                              {family.husband.displayName}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-500 dark:text-slate-400 italic">
                            {t('gedcom.unknown', 'Unknown')}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Wife */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        {family.wife ? (
                          <>
                            <span className="text-base sm:text-lg" title={t('gedcom.female', 'Female')}>
                              ♀
                            </span>
                            <span className="font-medium text-slate-900 dark:text-white">
                              {family.wife.displayName}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-500 dark:text-slate-400 italic">
                            {t('gedcom.unknown', 'Unknown')}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Marriage */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-slate-700 dark:text-slate-300">
                      {formatDateDisplay(family.marriageDate, family.marriageYear, family.marriageQualifier)}
                    </td>

                    {/* Divorce */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-slate-700 dark:text-slate-300">
                      {formatDateDisplay(family.divorceDate, family.divorceYear, family.divorceQualifier)}
                    </td>

                    {/* Children */}
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      {hasChildren ? (
                        <button
                          onClick={() => toggleRow(family.familyId)}
                          className="flex items-center gap-1.5 sm:gap-2 text-primary hover:text-primary/80 transition-colors"
                        >
                          <span className="font-medium">
                            {t('gedcom.childrenCount', {
                              count: family.children.length,
                              defaultValue: '{{count}} children',
                            })}
                          </span>
                          <span className="material-symbols-outlined text-base">
                            {isExpanded ? 'expand_less' : 'expand_more'}
                          </span>
                        </button>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">
                          {t('gedcom.childrenCount', { count: 0, defaultValue: '0 children' })}
                        </span>
                      )}

                      {/* Expanded Children List */}
                      {isExpanded && hasChildren && (
                        <div className="mt-2 pl-3 sm:pl-4 border-l-2 border-slate-200 dark:border-slate-600 space-y-1">
                          {family.children.map((child) => (
                            <div
                              key={child.gedcomId}
                              className="flex items-center gap-1.5 text-xs sm:text-sm"
                            >
                              <span className="text-slate-400 dark:text-slate-500">•</span>
                              <span className="text-slate-700 dark:text-slate-300">
                                {child.displayName}
                              </span>
                              <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                                ({child.gedcomId})
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
